import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import DashboardShell, { StatCard } from "@/components/DashboardShell";
import { api, fmtNGN, formatErr } from "@/lib/api";
import { CheckCircle, Lightning } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function SchoolTransactions() {
  const [d, setD] = useState({ transactions: [], summary: { gross: 0, fees: 0, net: 0, count: 0 } });
  const [params, setParams] = useSearchParams();
  const ackTarget = params.get("ack");

  const reload = async () => {
    const { data } = await api.get("/schools/transactions");
    setD(data);
  };
  useEffect(() => { reload(); }, []);

  // Auto-fire acknowledge when the school clicks the email CTA (?ack=<txn_id>)
  useEffect(() => {
    if (!ackTarget) return;
    (async () => {
      try {
        await api.post(`/schools/disbursements/${ackTarget}/acknowledge`);
        toast.success("Receipt acknowledged");
      } catch (e) {
        toast.error(formatErr(e?.response?.data?.detail) || e.message);
      } finally {
        params.delete("ack");
        setParams(params, { replace: true });
        reload();
      }
    })();
  }, [ackTarget]); // eslint-disable-line

  const acknowledge = async (txnId) => {
    try {
      await api.post(`/schools/disbursements/${txnId}/acknowledge`);
      toast.success("Receipt acknowledged");
      reload();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || e.message);
    }
  };

  return (
    <DashboardShell title="Collections ledger" subtitle="Every fee collection, net of platform fees. Loan disbursements appear here with FEE = ₦0.">
      <div className="grid lg:grid-cols-4 gap-6 mb-6">
        <StatCard label="Gross" value={fmtNGN(d.summary.gross)} accent="primary" />
        <StatCard label="Fees" value={fmtNGN(d.summary.fees)} accent="warning" />
        <StatCard label="Net" value={fmtNGN(d.summary.net)} accent="success" />
        <StatCard label="Transactions" value={d.summary.count} accent="primary" />
      </div>
      <div className="card">
        {d.transactions.length === 0 ? (
          <p className="text-sm text-[#4F5D4F] py-8 text-center">No transactions yet.</p>
        ) : (
          <table className="dense">
            <thead><tr><th>Date</th><th>Reference</th><th>Channel</th><th>Gross</th><th>Fee</th><th>Net</th><th></th></tr></thead>
            <tbody>
              {d.transactions.map((t) => {
                const isLoan = t.channel === "loan_disbursement";
                return (
                  <tr key={t.id} data-testid={`txn-${t.id}`}>
                    <td>{new Date(t.created_at).toLocaleString()}</td>
                    <td className="font-mono text-xs">{t.reference}</td>
                    <td>
                      {isLoan ? (
                        <span className="badge badge-success" data-testid={`channel-loan-${t.id}`}>
                          <Lightning size={11} weight="fill" /> loan disbursement
                        </span>
                      ) : (
                        <span className="badge badge-info">{t.channel}</span>
                      )}
                    </td>
                    <td className="font-mono">{fmtNGN(t.amount)}</td>
                    <td className="font-mono text-[#B45309]">{t.platform_fee ? `-${fmtNGN(t.platform_fee)}` : "—"}</td>
                    <td className="font-mono text-[#2D7A5D]">{fmtNGN(t.net_to_school)}</td>
                    <td>
                      {isLoan && (
                        t.school_acknowledged ? (
                          <span className="text-xs text-[#2D7A5D] inline-flex items-center gap-1" data-testid={`acked-${t.id}`}>
                            <CheckCircle size={14} weight="fill" /> Acknowledged
                          </span>
                        ) : (
                          <button
                            onClick={() => acknowledge(t.id)}
                            className="btn-outline !py-1 !px-3 text-xs"
                            data-testid={`acknowledge-${t.id}`}
                          >
                            Acknowledge receipt
                          </button>
                        )
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </DashboardShell>
  );
}
